// ./src/hooks/useVendorHistory.ts
//
// ROADMAP #202 — the record of what was decided about the documents asked of vendors: a server-paged feed, newest first (Load more,
// `total` from the result). Read only: the record is written by the server, by the paths that change a request.
// ⚠ It re-reads when the filter changes, and a page-level provider is not worth having for a list one screen reads.
import { useCallback, useEffect, useRef, useState } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { EventVendorHistory, EventVendorHistoryFilter, EventVendorService } from "@/utils/api";
import { upsertManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseVendorHistoryReturn {
  lines: EventVendorHistory[];
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  total: number;
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;
  lastError: OpResult | null;
  clearError: () => void;
}

export const useVendorHistory = (filter: EventVendorHistoryFilter): UseVendorHistoryReturn => {
  const { user } = useApp();
  const userId = user?.id ?? null;
  const filterKey = JSON.stringify(filter);
  const filterRef = useRef(filter);
  const callIdRef = useRef(0);

  filterRef.current = filter;

  const [lines, setLines] = useState<EventVendorHistory[]>([]);
  // ⚠ Starts TRUE — "about to read" is not "read, and empty".
  const [loading, setLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // ⚠ A line written between two pages moves the rest along, so the rows on screen may never reach `total`: a short page is what says it ended.
  const [reachedEnd, setReachedEnd] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);

  const hasMore = !reachedEnd && lines.length < total;

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const read = useCallback(async (targetPage: number): Promise<OpResult> => {
    const callId = ++callIdRef.current;
    const isAppending = targetPage > 1;

    if (isAppending) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLoadingMore(false);
    }

    setLastError(null);

    try {
      const result = await EventVendorService.listHistory(filterRef.current, { page: targetPage, pageSize: DEFAULT_PAGE_SIZE });

      if (callId !== callIdRef.current) {
        return result;
      }

      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        const rows = result.data as EventVendorHistory[];

        setLines((prev) => (isAppending ? upsertManyBy(prev, rows, "id", "end") : rows));
        setTotal(typeof (result as any).total === "number" ? (result as any).total : rows.length);
        setPage(targetPage);
        setReachedEnd(rows.length < DEFAULT_PAGE_SIZE);
        setIsLoaded(true);

        return result;
      }

      setLastError(result);
      setIsLoaded(true);

      if (!isAppending) {
        setLines([]);
        setTotal(0);
      }

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);

      if (callId === callIdRef.current) {
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setLines([]);
          setTotal(0);
        }
      }

      return result;
    } finally {
      if (callId === callIdRef.current) {
        if (isAppending) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    await read(page + 1);
  }, [read, loading, loadingMore, hasMore, page]);

  const refresh = useCallback(() => read(1), [read]);

  useEffect(() => {
    if (userId) {
      void read(1);
    }
  }, [userId, filterKey, read]);

  return { lines, loading, isLoaded, loadingMore, hasMore, total, loadMore, refresh, lastError, clearError };
};
